// Main-pattern selection — Detection Engine System 1 (spec §3.2 / §3.3).
//
// Один выбор основного паттерна питает ОБА рендера (красная линия + верхняя §V
// карточка). Порядок: внутрисемейные mutex (§3.2) → межсемейная лестница тиров
// (§3.3 step 2) → тай-брейк по силе отрыва (step 3). Детерминированно, FP=0.

import { layerIndex } from './layers.js';
import { gapAt, othersMeanAt } from './input.js';
import { THRESHOLDS as T } from './thresholds.js';

// Тиры (§3.3): 1 — событие страницы; 2 — линия вырвалась / поле раскололось;
// 3 — линии вместе; 4 — scatter (только если выше пусто).
const TIER = { field: 1, singleton: 2, opposition: 2, together: 3, untiered: 4 };

// Фиксированный порядок семейств для полного тай-брейка (§3.3 step 3).
const FAMILY_ORDER = { field: 0, singleton: 1, opposition: 2, together: 3, scatter: 4 };

/**
 * Внутрисемейные mutex (§3.2). Снимают дубли одной линии/группы ДО межсемейного
 * сравнения. Также аннотирует wanting CS-below routing (§3.2 rule 4).
 * Возвращает отфильтрованный список detection-объектов.
 */
export function applyMutex(detections, stages, active) {
  let survivors = detections.slice();

  // Rule 1 — field-level-recovery суппрессит per-layer v-shape (s203).
  const hasField = survivors.some((d) => d.type === 'field-level-recovery');
  if (hasField)
    survivors = survivors.filter((d) => d.type !== 'v-shape-recovery');

  // Rule 2 — v-shape ↔ current-singleton guard (s201, гейтинг как в прототипе).
  // v-shape считается реальным recovery только если |L(N)−mean(other)| ≤ 1.5;
  // иначе поздний «возврат» — это линия, стоящая в стороне (current-singleton),
  // а не яма-с-возвратом → снимаем v-shape. Поскольку current-singleton требует
  // |gap(N)|≥2 > 1.5, оба никогда не выигрывают вместе (Hannah=v-shape;
  // Charlotte: guard=4.67 → v-shape снят, остаётся current-singleton).
  survivors = survivors.filter(
    (d) => d.type !== 'v-shape-recovery' || d.guard <= T.VSHAPE_GUARD,
  );
  const byLayer = (type, L) =>
    survivors.find((d) => d.type === type && d.layers[0] === L);

  // Rule 3 — singleton-cycling ↔ historical-singleton (s201): cycling wins.
  for (const L of active) {
    const cyc = byLayer('singleton-cycling', L);
    const hist = byLayer('historical-singleton', L);
    if (cyc && hist) survivors = survivors.filter((d) => d !== hist);
  }

  // Rule 5 — §3.3 set-assembly (s315, web-Claude ruling 2026-07-24). A parallel-whole
  // PAIR whose both members are contained in a larger mutually-parallel `together`
  // group is a sub-group of it → suppress the pair so the MAXIMAL co-parallel set
  // carries (pair=2 · triplet=3 · field=4+; ≥3 co-parallel never collapse to a pair).
  // Carrier CURVE is unchanged (still the group mean — carrier.js together case);
  // this only fixes the carrier-SET (who carries), a separate axis from geometry_class
  // (s298.d3). Fixes the §2.9 triplet/field rows the old tie-break dropped to a pair.
  const coParallelGroups = survivors.filter(
    (d) => d.family === 'together' && d.type === 'triplet-as-one',
  );
  if (coParallelGroups.length) {
    survivors = survivors.filter((d) => {
      if (d.family === 'together' && d.type === 'parallel-whole') {
        const subsumed = coParallelGroups.some(
          (g) => g.layers.length > d.layers.length && d.layers.every((L) => g.layers.includes(L)),
        );
        if (subsumed) return false;
      }
      return true;
    });
  }

  // Rule 4 — wanting · CS-below routing (s214, FP=0).
  for (const d of survivors) {
    if (d.type === 'current-singleton' && d.layers[0] === 'Wanting') {
      const N = stages.length;
      const gapN = gapAt(stages, active, 'Wanting', N - 1);
      if (gapN <= -T.SINGLETON_GAP) {
        let peak = -Infinity;
        for (let s = 0; s < N - 1; s++) {
          const g = stages[s].values.Wanting - othersMeanAt(stages, active, 'Wanting', s);
          if (g > peak) peak = g;
        }
        d.routing =
          peak >= T.WANTING_VACUUM_PEAK ? 'A.2.5.1.#11' : 'A.2.5.1.#9'; // вакуум / подавлено
      }
    }
  }

  return survivors;
}

/** Scatter-sentinel: паттерн не найден (§2.1 / §3.3 Tier 4). */
export function scatterPattern(active) {
  return { family: 'scatter', type: 'scatter', layers: active.slice(), strength: 0 };
}

/**
 * Выбор основного паттерна из выживших конфигураций (§3.3 step 2–3).
 * @returns { main, tier, ranked } — ranked = survivors, отсортированные для
 *          §V-карточек (main первой, далее по силе отрыва).
 */
export function selectMainPattern(survivors, active) {
  const tiered = survivors.filter((d) => TIER[d.family] <= 3);
  if (tiered.length === 0) {
    const main = scatterPattern(active);
    return { main, tier: 4, ranked: [main] };
  }

  // Высший непустой tier (1 бьёт 2 бьёт 3).
  const minTier = Math.min(...tiered.map((d) => TIER[d.family]));
  const inTier = tiered.filter((d) => TIER[d.family] === minTier);

  const sorted = inTier.slice().sort(cmpStrength);
  const main = sorted[0];

  // ranked для карточек: main + остальные выжившие (вкл. нижние tier) по силе.
  const rest = survivors
    .filter((d) => d !== main && d.family !== 'untiered')
    .sort(cmpStrength);
  return { main, tier: minTier, ranked: [main, ...rest] };
}

/** Тай-брейк (§3.3 step 3): сила ↓, затем фикс. порядок family, затем L1..L7. */
function cmpStrength(a, b) {
  if (b.strength !== a.strength) return b.strength - a.strength;
  const fa = FAMILY_ORDER[a.family] ?? 9;
  const fb = FAMILY_ORDER[b.family] ?? 9;
  if (fa !== fb) return fa - fb;
  return layerIndex(a.layers[0]) - layerIndex(b.layers[0]);
}

/**
 * Полный путь выбора: mutex → лестница. Удобная обёртка.
 */
export function resolve(detections, stages, active) {
  const survivors = applyMutex(detections, stages, active);
  return selectMainPattern(survivors, active);
}
